<!-- Responsibility: Render the reusable companion sheet dialog content surface.
Scope: Companion sheet dialog content only; modal lifecycle is owned by game-modal/app-shell. -->
<companion-sheet-dialog class="fm-companion-sheet" hidden={ !state.sheet }>
  <div if={ state.sheet } style="display: contents;">
  <header class="fm-header">
    <div class="fm-header-copy">
      <h2 if={ state.title }>{ state.title }</h2>
      <raw-html if={ state.desc } content={ state.desc } mode="prose" class="fm-desc"></raw-html>
      <raw-html if={ state.meta } content={ state.meta } mode="prose" class="fm-meta"></raw-html>
    </div>
    <button class="close-btn modal-close-hit" aria-label="Close companion dialog" onclick={ closeDialog }>X</button>
  </header>

  <button    class="fm-companion-portrait-btn float-media { state.portraitZoomed ? 'zoomed' : '' }"
    data-tip="Toggle portrait zoom"
    data-tip-side="top"
    aria-label="Toggle companion portrait zoom"
    onclick={ togglePortraitZoom }
  >
    <img if={ state.imageUrl } src={ state.imageUrl } class="fm-companion-portrait" alt={ state.title }>
    <span if={ !state.imageUrl } class="fm-companion-portrait-fallback">{ state.imageFallbackLabel }</span>
  </button>

  <div class="fm-companion-main">
    <div class="fm-section fm-hero">
      <h2 class="fm-companion-name">{ state.title }</h2>
      <p if={ state.bio } class="fm-bio-text">{ state.bio }</p>
      <div if={ state.meta } class="fm-meta-text">{ state.meta }</div>
      <vitals vitals={ state.sheet?.vitals }></vitals>
    </div>

    <nav class="fm-tabs">
      <ul>
        <li each={ tab in state.tabs }>
          <button
            class={ state.tab === tab.id ? 'active' : '' }
            onclick={ () => setTab(tab.id) }
          >{ tab.label }</button>
        </li>
      </ul>
    </nav>

    <div if={ state.tab === 'profile' && state.sheet?.abilities?.length } class="fm-section fm-companion-section">
      <div class="fm-cards-label">ABILITIES</div>
      <div class="fm-companion-tags">
        <button
          each={ ab in state.sheet?.abilities }
          class="fm-chip fm-tag ability actionable"
          data-tip={ abilityTip(ab) }
          data-tip-side="top"
          aria-label={ `Inspect ability ${ab.name}` }
          onclick={ () => openAbility(ab) }
        >
          <b>{ ab.name }</b>
          <span>{ abilitySummary(ab) }</span>
        </button>
      </div>
      <div if={ (state.sheet?.overflow?.abilities ?? 0) > 0 } class="fm-overflow-note">
        +{ state.sheet?.overflow.abilities } more abilities hidden for stable rendering.
      </div>
    </div>

    <div if={ state.tab === 'profile' && state.sheet?.traits?.length } class="fm-section fm-companion-section">
      <div class="fm-cards-label">TRAITS</div>
      <div class="fm-companion-tags">
        <button
          each={ trait in state.sheet?.traits }
          class="fm-chip fm-tag trait actionable"
          data-tip={ traitInspectTip(trait) }
          data-tip-side="top"
          aria-label={ `Inspect trait ${trait.name}` }
          onclick={ () => openTraitInfo(trait) }
        >
          <b>{ trait.name }</b>
          <span>{ trait.desc || 'No description.' }</span>
        </button>
      </div>
      <div if={ (state.sheet?.overflow?.traits ?? 0) > 0 } class="fm-overflow-note">
        +{ state.sheet?.overflow.traits } more traits hidden for stable rendering.
      </div>
    </div>

    <div if={ state.tab === 'inventory' } class="fm-section fm-companion-section">
      <div class="fm-cards-label">INVENTORY</div>
      <div class="fm-companion-tags" if={ state.sheet?.inventory?.length }>
        <button
          each={ itemName in state.sheet?.inventory }
          class="fm-chip fm-tag inventory actionable"
          data-tip={ inventoryInspectTip(itemName, 'Companion item') }
          data-tip-side="top"
          aria-label={ `Inspect companion item ${itemName}` }
          onclick={ () => this.props.content?.onOpenItem?.(itemName) }
        >
          <object-visual name={ itemName } size="sm" icon-only={true}></object-visual>
        </button>
      </div>
      <div if={ (state.sheet?.overflow?.inventory ?? 0) > 0 } class="fm-overflow-note">
        +{ state.sheet?.overflow.inventory } more inventory entries hidden.
      </div>
      <div if={ !state.sheet?.inventory?.length } class="fm-companion-empty">Inventory empty.</div>
    </div>

    <div if={ state.tab === 'effects' } class="fm-section fm-companion-section">
      <div if={ state.sheet?.activeEffects?.length } class="fm-cards-label">ACTIVE EFFECTS</div>
      <div if={ state.sheet?.activeEffects?.length } class="fm-companion-tags">
        <span each={ effect in state.sheet?.activeEffects } class="fm-effect-chip active">{ this.props.content?.effectChipText?.(effect) }</span>
      </div>
      <div if={ (state.sheet?.overflow?.activeEffects ?? 0) > 0 } class="fm-overflow-note">
        +{ state.sheet?.overflow.activeEffects } more active effects hidden.
      </div>
      <div if={ state.sheet?.dormantEffects?.length } class="fm-cards-label">DORMANT EFFECTS</div>
      <div if={ state.sheet?.dormantEffects?.length } class="fm-companion-tags">
        <span each={ effect in state.sheet?.dormantEffects } class="fm-effect-chip dormant">{ this.props.content?.effectChipText?.(effect, true) }</span>
      </div>
      <div if={ (state.sheet?.overflow?.dormantEffects ?? 0) > 0 } class="fm-overflow-note">
        +{ state.sheet?.overflow.dormantEffects } more dormant effects hidden.
      </div>
    <div if={ !state.sheet?.activeEffects?.length && !state.sheet?.dormantEffects?.length } class="fm-companion-empty">No active or dormant effects.</div>
    </div>
  </div>

  <footer if={ state.actions.length } class="modal-actions">
    <div class="fm-chips">
      <button
        each={ action in state.actions }
        class="fm-chip { action.danger ? 'danger' : '' }"
        onclick={ () => onAction(action) }
      >{ action.label }</button>
    </div>
  </footer>
  </div>

  <script>
    import { playEffect } from '../../../engine/audio.js'
    import { runUiAction } from '../../action-manager.ts'
    import { openInfoDialog } from '../../dialog/dialog-openers.ts'
    import ObjectVisual from '../object-visual.riot'
    import RawHtml from '../raw-html.riot'
    import Vitals from '../vitals.riot'

    export default {
      components: { ObjectVisual, RawHtml, Vitals },
      state: {
        tab: 'profile',
        tabs: [
          { id: 'profile', label: 'PROFILE' },
          { id: 'inventory', label: 'INVENTORY' },
          { id: 'effects', label: 'EFFECTS' }
        ],
        portraitZoomed: false,
        sheet: null,
        imageUrl: null,
        title: '',
        desc: '',
        bio: '',
        meta: '',
        imageFallbackLabel: '?',
        actions: [],
      },

      onMounted() {
        this.openToken = Number(this.props.openToken || 0)
        this.syncState(this.props)
        this.update()
      },

      onBeforeUpdate(props, state) {
        const token = Number(props.openToken || 0)
        if (token !== this.openToken) {
          this.openToken = token
          state.tab = 'profile'
          state.portraitZoomed = false
        }
        this.syncState(props)
      },

      syncState(props) {
        const content = props.content ?? {}
        const options = props.options ?? {}
        const sheet = content.sheet ?? null
        const title = content.title ?? ''
        
        const nextState = {
          sheet,
          title,
          desc: String(options.desc ?? content.desc ?? '').trim(),
          imageUrl: content.imageUrl ?? null,
          bio: String(sheet?.bio ?? '').trim(),
          meta: String(options.meta ?? content.meta ?? '').trim(),
          actions: Array.isArray(options.actions) ? options.actions : [],
          imageFallbackLabel: this.buildFallbackLabel(title)
        }
        
        Object.assign(this.state, nextState)
      },

      buildFallbackLabel(title) {
        const src = String(title || '').trim()
        if (!src) return '?'
        return src
          .split(/\s+/)
          .filter(Boolean)
          .slice(0, 2)
          .map(s => s[0]?.toUpperCase() ?? '')
          .join('') || '?'
      },

      togglePortraitZoom() {
        this.update({ portraitZoomed: !this.state.portraitZoomed })
      },

      setTab(tab) {
        this.update({ tab: tab === 'inventory' || tab === 'effects' ? tab : 'profile' })
      },

      abilitySummary(ability) {
        const desc = String(ability?.desc ?? '').trim()
        const cooldown = Number(ability?.cooldown ?? 0)
        if (desc) return desc
        if (cooldown > 0) return `Cooldown ${cooldown}m`
        return 'Ability'
      },

      abilityTip(ability) {
        const lines = [
          `Ability · ${String(ability?.name ?? '').trim() || 'Unknown'}`,
          String(ability?.desc ?? '').trim() || 'No description recorded.',
        ]
        const cooldown = Number(ability?.cooldown ?? 0)
        if (cooldown > 0) lines.push(`Cooldown ${cooldown}m`)
        lines.push('Tap to inspect')
        return lines.join('\n')
      },

      traitInspectTip(trait) {
        return [
          `Trait · ${String(trait?.name ?? '').trim() || 'Unknown'}`,
          String(trait?.desc ?? '').trim() || 'No description recorded.',
          'Tap to inspect',
        ].join('\n')
      },

      inventoryInspectTip(itemName, label) {
        return [
          `${label} · ${String(itemName ?? '').trim() || 'Unknown item'}`,
          'Tap to inspect full item details',
        ].join('\n')
      },

      openAbility(ability) {
        playEffect('ui_click')
        const abilityTitle = String(ability?.name ?? '').trim() || 'Ability'
        const desc = String(ability?.desc ?? '').trim() || 'No description recorded.'
        const cooldown = Number(ability?.cooldown ?? 0)
        openInfoDialog('companion-sheet-dialog', {
          options: {
            title: abilityTitle,
            desc,
            meta: cooldown > 0 ? `COMPANION ABILITY · cooldown ${cooldown}m` : 'COMPANION ABILITY',
          },
        })
      },

      openTraitInfo(trait) {
        playEffect('ui_click')
        const traitTitle = String(trait?.name ?? '').trim() || 'Trait'
        openInfoDialog('companion-sheet-dialog', {
          options: {
            title: traitTitle,
            desc: String(trait?.desc ?? '').trim() || 'No description recorded.',
            meta: 'COMPANION TRAIT',
          },
        })
      },

      onAction(action) {
        playEffect('ui_click')
        this.props.requestModalClose?.('action').then((closed) => {
          if (closed === false) return
          return runUiAction(action, {
            initiatorId: 'companion-sheet-dialog',
            targetName: this.state.title || undefined,
            targetType: 'companion',
          })
        }).catch((error) => console.warn('Action denied:', error))
      },

      async closeDialog() {
        await this.props.requestModalClose?.('close-button')
      },
    }
  </script>

  <style>
    companion-sheet-dialog {
      display: block;
      overflow: auto;
      margin-bottom: var(--space-xs);
    }

    .fm-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: var(--space-md);
      margin-bottom: var(--space-sm);
    }

    .fm-header-copy {
      min-width: 0;
    }

    .fm-header h2 {
      margin: 0 0 var(--space-xs);
    }

    .float-media {
      float: left;
      width: 40% !important;
      margin-right: var(--space-md);
      margin-bottom: var(--space-xs);
      border-radius: 0.5rem;
      overflow: hidden;
      border: 1px solid var(--border);
      background: var(--bg);
      padding: 0;
      cursor: zoom-in;
      transition: transform 0.22s ease, border-color 0.18s ease;
      
      &:hover {
        border-color: var(--gold);
      }

      &.zoomed {
        cursor: zoom-out;
        transform: scale(1.8);
        transform-origin: top left;
        z-index: 4;
        position: relative;
      }
    }

    .fm-companion-portrait {
      width: 100%;
      height: auto;
      display: block;
      object-fit: cover;
    }

    .fm-companion-portrait-fallback {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 7.5rem;
      color: var(--gold);
      font-weight: bold;
      letter-spacing: 0.1em;
      font-size: var(--space-md);
    }

    .fm-companion-main {
      min-width: 0;
    }

    .fm-hero {
      display: flex;
      flex-direction: column;
      gap: var(--space-xs);
      margin-bottom: var(--space-sm);
    }

    .fm-companion-name {
      margin: 0;
      color: var(--gold);
      font-size: var(--space-md);
      letter-spacing: 0.05em;
      text-transform: uppercase;
    }

    .fm-bio-text {
      margin: 0;
      color: var(--text-prose);
      line-height: 1.5;
      font-size: 0.95rem;
    }

    .fm-meta-text {
      font-size: var(--type-3xs);
      color: var(--text-muted);
      letter-spacing: 0.05em;
    }

    .fm-tabs {
      margin-bottom: var(--space-sm);
      clear: both;
    }

    .fm-tabs ul {
      display: flex;
      flex-wrap: wrap;
      gap: var(--space-xs);
      list-style: none;
      margin: 0;
      padding: 0;
    }

    .fm-tabs button {
      border: 1px solid var(--border);
      background: var(--bg);
      color: var(--text-muted);
      border-radius: 999rem;
      font-size: var(--type-3xs);
      letter-spacing: 0.08em;
      padding: var(--space-xs) var(--space-sm);
      cursor: pointer;
      font-family: inherit;
      transition: all 0.16s ease;

      &:hover:not(.active) {
        border-color: var(--alpha-mid);
        color: var(--text-prose);
      }

      &.active {
        color: var(--gold);
        border-color: var(--gold);
        background: var(--surface-card-3);
      }
    }

    .fm-section {
      display: flex;
      flex-direction: column;
      gap: var(--space-xs);
      margin-bottom: var(--space-md);
    }

    .fm-cards-label {
      color: var(--gold);
      letter-spacing: 0.1em;
      font-weight: 800;
      font-size: var(--type-3xs);
      margin-bottom: var(--space-xs);
    }

    .fm-companion-tags {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(min(100%, 14rem), 1fr));
      gap: var(--space-xs);
    }

    button.actionable {
      border: 1px solid var(--border);
      background: linear-gradient(180deg, var(--surface-card-2), var(--bg));
      border-radius: 0.5rem;
      padding: var(--space-xs);
      display: flex;
      flex-direction: column;
      gap: var(--space-xs);
      cursor: pointer;
      text-align: left;
      transition: all 0.16s ease;

      &:hover {
        border-color: var(--gold);
        background: var(--surface-card-1);
        transform: translateY(-1px);
      }

      b {
        font-size: var(--space-sm);
        color: var(--gold);
      }

      span {
        font-size: var(--type-3xs);
        color: var(--text-muted);
        line-height: 1.4;
      }
    }

    .fm-effect-chip {
      border-radius: 999rem;
      padding: 0.18rem var(--space-xs);
      font-size: var(--type-3xs);
      border: 1px solid var(--border);
      background: var(--bg);
      color: var(--text-muted);
      font-weight: 700;

      &.active {
        border-color: var(--gold);
        color: var(--gold);
        box-shadow: 0 0 0.5rem var(--gold-glow);
      }

      &.dormant {
        opacity: 0.6;
      }
    }

    .fm-overflow-note {
      font-size: var(--type-3xs);
      color: var(--text-dim);
      margin-top: var(--space-xs);
    }

    .fm-companion-empty {
      color: var(--text-dim);
      font-style: italic;
    }

    @media (max-width: 30rem) {
      .float-media {
        float: none;
        width: 100% !important;
        margin-right: 0;
      }
    }
  </style>
</companion-sheet-dialog>
